'use client';

import { FC, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import Image from 'next/image';
import { Calendar, X } from 'lucide-react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import dayjs from 'dayjs';
import { useLocale, useTranslations } from 'next-intl';
import { isValidPhoneNumber } from 'libphonenumber-js/max';
import AppPhoneInput from '@/hotdesk-ui/components/shared/AppPhoneInput';
import DateSelectorPopover from '@/hotdesk-ui/components/booking-v4/DateSelectorPopover';
import { useAppDispatch, useAppSelector } from '@/hotdesk-ui/hooks/hook';
import { RootState } from '@/hotdesk-ui/store/store';
import { fetchListingTypes } from '@/hotdesk-ui/store/slices/listing-types.slice';
import { firebaseAnalytics } from '@/hotdesk-ui/common-classes/firebase.service';
import { LongTermQuoteSubmitPayload, LongTermQuoteTarget, LongTermUserPrefill } from './longTermQuote.types';
import { searchCities } from '@/hotdesk-ui/helpers/city-search';
import SearchSelect from '@/hotdesk-ui/components/shared/SearchSelect';
import SelectMenu, { type SelectMenuOption } from '@/hotdesk-ui/components/shared/SelectMenu';
import { hasFlutterBridge } from '@/hotdesk-ui/webview-bridge/flutterChannel';
import styles from './LongTermBookingModal.module.scss';
interface LongTermBookingModalProps {
  show: boolean;
  target: LongTermQuoteTarget | null;
  onClose: () => void;
  /** `inline` / `page` render on the page; `modal` is the popup. */
  variant?: 'modal' | 'inline' | 'page';
  /** Optional contact prefill (WebView / Flutter). */
  userPrefill?: LongTermUserPrefill | null;
  /**
   * Webview page only: ignore the website session. Fill/lock contact
   * fields from `userPrefill` (Flutter), or leave them empty.
   */
  ignoreWebsiteAuth?: boolean;
  /** Hide the X button (WebView pages use Done after submit). */
  hideCloseButton?: boolean;
  /** Fired after a successful sheet write (webview `formSubmitted`). */
  onSubmitted?: () => void;
}
interface FormValues {
  fullName: string;
  email: string;
  phone: string;
  city: string;
  workspace: string;
  listing: string;
  listingTypeId: string;
  guests: string;
  startDate: string;
  source: string;
}

/**
 * Guest-count ranges; `to: null` is the open-ended "50+". The value (e.g.
 * "10-19", "50+") is what reaches Attio / the Sheet as "Guests".
 */
const GUEST_RANGES: ReadonlyArray<{
  from: number;
  to: number | null;
}> = [{
  from: 1,
  to: 9
}, {
  from: 10,
  to: 19
}, {
  from: 20,
  to: 29
}, {
  from: 30,
  to: 50
}, {
  from: 50,
  to: null
}];
/** A few spare results so de-duplication can't leave fewer than the 4 shown. */
const CITY_SEARCH_LIMIT = 10;

/** City suggestions for SearchSelect: name, with the country in grey. */
const searchCityOptions = async (query: string) => (await searchCities(query, CITY_SEARCH_LIMIT)).map(c => ({
  id: c.id,
  label: c.name,
  secondary: c.countryName
}));
const LongTermBookingModal: FC<LongTermBookingModalProps> = ({
  show,
  target,
  onClose,
  variant = 'modal',
  userPrefill = null,
  ignoreWebsiteAuth = false,
  hideCloseButton = false,
  onSubmitted
}) => {
  const isPage = variant === 'page';
  const isInline = variant === 'inline' || isPage;
  const t = useTranslations('longTermBooking');
  const locale = useLocale();
  const dispatch = useAppDispatch();
  const user = useAppSelector((s: RootState) => s.auth.user);
  const listingTypes = useAppSelector((s: RootState) => s.listingTypes.items);
  const [mounted, setMounted] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [dateOpen, setDateOpen] = useState(false);
  const isFromCard = target?.source === 'IWG';
  const lockCity = isFromCard && !!target?.city?.trim();
  const lockWorkspace = isFromCard && !!target?.spaceName;
  const lockListing = isFromCard && !!target?.listingName;
  const lockListingType = isFromCard && !!target?.listingTypeId;
  useEffect(() => {
    setMounted(true);
  }, []);
  useEffect(() => {
    if (!show) return;
    if (listingTypes.length === 0) {
      dispatch(fetchListingTypes(locale));
    }
  }, [show, listingTypes.length, dispatch, locale]);
  useEffect(() => {
    if (!show || isInline) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [show, onClose, isInline]);
  useEffect(() => {
    if (!show) {
      setSubmitted(false);
      setDateOpen(false);
      setSubmitError(null);
    }
  }, [show]);
  const websiteUser = ignoreWebsiteAuth ? null : user;
  const fullName = [websiteUser?.firstName, websiteUser?.lastName].filter(Boolean).join(' ').trim();
  const accountFullName = ignoreWebsiteAuth ? userPrefill?.fullName || '' : fullName;
  const accountEmail = ignoreWebsiteAuth ? userPrefill?.email || '' : websiteUser?.email || '';
  const accountPhone = ignoreWebsiteAuth ? userPrefill?.phoneNumber || '' : websiteUser?.phoneNumber || '';
  const isLoggedIn = ignoreWebsiteAuth ? !!userPrefill?.fromAccount : !!websiteUser?.id;
  const lockName = isLoggedIn && !!accountFullName;
  const lockEmail = isLoggedIn && !!accountEmail;
  const lockPhone = isLoggedIn && (ignoreWebsiteAuth ? !!userPrefill?.phoneVerified : !!websiteUser?.isPhoneVerified);
  const schema = useMemo(() => yup.object({
    fullName: yup.string().required(t('errors.required')),
    email: yup.string().email(t('errors.email')).required(t('errors.required')),
    phone: yup.string().required(t('errors.required')).test('valid-phone', t('errors.phone'), value => !!value && isValidPhoneNumber(value)),
    city: yup.string().trim().required(t('errors.required')),
    workspace: yup.string().default(''),
    listing: yup.string().default(''),
    listingTypeId: yup.string().required(t('errors.required')),
    guests: yup.string().required(t('errors.required')),
    startDate: yup.string().required(t('errors.required')),
    source: yup.string().required()
  }), [t]);
  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    getValues,
    formState: {
      errors
    }
  } = useForm<FormValues>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      fullName: '',
      email: '',
      phone: '',
      city: '',
      workspace: '',
      listing: '',
      listingTypeId: '',
      guests: '',
      startDate: '',
      source: 'banner'
    }
  });
  const cityValue = watch('city');
  const guestOptions = useMemo<SelectMenuOption[]>(() => GUEST_RANGES.map(({
    from,
    to
  }) => {
    const sign = to === null ? '+' : '–';
    // The open-ended "50+" reads fine on its own.
    const unit = to === null ? '' : t('guestsUnit');
    return {
      value: to === null ? `${from}+` : `${from}-${to}`,
      label: `${from}${sign}${to ?? ''}${unit ? ` ${unit}` : ''}`,
      // Tight numbers ("1–9"), in a box as wide as the widest
      // range (an invisible "10–19" sharing the cell), so the
      // word after it starts at the same point on every row.
      content: <span className={styles.guestRange}>
                            <span className={styles.guestNumbers}>
                                <span>
                                    {from}
                                    {sign}
                                    {to ?? ''}
                                </span>
                                <span className={styles.guestNumbersSizer} aria-hidden>
                                    10–19
                                </span>
                            </span>
                        </span>
    };
  }), [t]);
  const listingTypeOptions = useMemo<SelectMenuOption[]>(() => listingTypes.map(lt => ({
    value: String(lt.id),
    label: lt.type,
    icon: lt.icon ?
    // eslint-disable-next-line @next/next/no-img-element
    <img src={lt.icon} alt="" /> : undefined
  })), [listingTypes]);
  useEffect(() => {
    if (!show || !target) return;
    const cityPrefill = (target.city || '').trim();
    reset({
      fullName: ignoreWebsiteAuth ? '' : fullName,
      email: ignoreWebsiteAuth ? '' : websiteUser?.email || '',
      phone: ignoreWebsiteAuth ? '' : websiteUser?.phoneNumber || '',
      city: cityPrefill,
      workspace: target.spaceName || '',
      listing: target.listingName || '',
      listingTypeId: target.listingTypeId ? String(target.listingTypeId) : '',
      guests: '',
      startDate: '',
      source: target.source
    });
    setSubmitted(false);
    setSubmitError(null);
  }, [show, target, ignoreWebsiteAuth, fullName, websiteUser?.email, websiteUser?.phoneNumber, reset]);

  // Flutter account prefill on the webview page. Guest / no bridge → empty.
  useEffect(() => {
    if (!show || !ignoreWebsiteAuth || !userPrefill?.fromAccount) return;
    if (userPrefill.fullName) {
      setValue('fullName', userPrefill.fullName, {
        shouldValidate: true
      });
    }
    if (userPrefill.email) {
      setValue('email', userPrefill.email, {
        shouldValidate: true
      });
    }
    if (userPrefill.phoneNumber) {
      if (userPrefill.phoneVerified || !getValues('phone')) {
        setValue('phone', userPrefill.phoneNumber, {
          shouldValidate: true
        });
      }
    }
    if (userPrefill.city && !getValues('city')) {
      setValue('city', userPrefill.city, {
        shouldValidate: true
      });
    }
  }, [show, ignoreWebsiteAuth, userPrefill, getValues, setValue]);
  const onSubmit = async (data: FormValues) => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const cityName = data.city.trim();
      const listingTypeName = listingTypes.find(lt => String(lt.id) === data.listingTypeId)?.type || data.listingTypeId;
      const payload: LongTermQuoteSubmitPayload = {
        fullName: lockName ? accountFullName || data.fullName.trim() : data.fullName.trim(),
        email: lockEmail ? accountEmail || data.email.trim() : data.email.trim(),
        phone: lockPhone ? accountPhone || data.phone.trim() : data.phone.trim(),
        city: cityName,
        workspace: data.workspace.trim(),
        listing: data.listing.trim(),
        listingType: listingTypeName,
        guests: data.guests,
        startDate: data.startDate,
        source: data.source === 'IWG' ? 'IWG' : 'banner',
        sourceChannel: hasFlutterBridge() ? 'mobile' : 'desktop',
        listingId: target?.listingId ?? null,
        workspaceId: target?.workspaceId ?? null
      };
      const res = await fetch('/api/long-term-quote', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        // The route returns a short code (e.g. invalid_email); log it
        // and show the user the translated message.
        const err = await res.json().catch(() => ({}));
        console.error('Long-term quote submit failed', res.status, err?.error);
        throw new Error(t('errors.submit'));
      }
      firebaseAnalytics('long_term_quote_submitted', {
        listing_id: payload.listingId,
        workspace_id: payload.workspaceId,
        city: payload.city,
        workspace: payload.workspace,
        listing: payload.listing,
        listing_type: payload.listingType,
        guests: payload.guests,
        start_date: payload.startDate,
        source: payload.source,
        source_channel: payload.sourceChannel
      });
      onSubmitted?.();
      setSubmitted(true);
    } catch (err) {
      console.error(err);
      setSubmitError(err instanceof Error ? err.message : t('errors.submit'));
    } finally {
      setSubmitting(false);
    }
  };
  if (!show || !target) return null;
  if (!isInline && !mounted) return null;
  const content = <div role={isInline ? 'region' : 'dialog'} aria-modal={isInline ? undefined : true} aria-labelledby="long-term-booking-title" className={styles.dialog}>
            {!hideCloseButton && <button type="button" className={styles.closeBtn} onClick={onClose} aria-label={t('close')}>
                    <X size={18} strokeWidth={2} />
                </button>}

            <div className={styles.dialogBody}>
            <aside className={styles.aside}>
                <Image src="/assets/icons/long-term.svg" alt="" width={88} height={108} className={styles.morphy} unoptimized />
                <div className={styles.asideHeading}>
                    <h2 id="long-term-booking-title" className={styles.asideTitle}>
                        {t('title')}
                    </h2>
                    <p className={styles.asideSubtitle}>{t('subtitle')}</p>
                </div>
                <p className={styles.asideBody}>{t('description')}</p>
            </aside>

            <div className={styles.formPane}>
                {submitted ? <div className={styles.success}>
                        <h3 className={styles.successTitle}>
                            {t('successTitle')}
                        </h3>
                        <p className={styles.successBody}>{t('successBody')}</p>
                        <button type="button" className={styles.submit} onClick={onClose}>
                            {t('done')}
                        </button>
                    </div> : <form className={styles.form} onSubmit={handleSubmit(onSubmit)} noValidate>
                        <input type="hidden" {...register('source')} />

                        <label className={styles.field}>
                            <span className={styles.label}>
                                {t('fullName')}
                                <span className={styles.req} aria-hidden>
                                    *
                                </span>
                            </span>
                            <input className={`${styles.input} ${lockName ? styles.inputMuted : ''}`} placeholder={t('fullName')} readOnly={lockName} {...register('fullName')} />
                            {errors.fullName && <span className={styles.error}>
                                    {errors.fullName.message}
                                </span>}
                        </label>

                        <label className={styles.field}>
                            <span className={styles.label}>
                                {t('email')}
                                <span className={styles.req} aria-hidden>
                                    *
                                </span>
                            </span>
                            <input className={`${styles.input} ${lockEmail ? styles.inputMuted : ''}`} type="email" placeholder={t('email')} readOnly={lockEmail} {...register('email')} />
                            {errors.email && <span className={styles.error}>
                                    {errors.email.message}
                                </span>}
                        </label>

                        <div className={styles.field}>
                            <span className={styles.label}>
                                {t('phone')}
                                <span className={styles.req} aria-hidden>
                                    *
                                </span>
                            </span>
                            <div className={`${styles.phoneWrap} ${lockPhone ? styles.phoneWrapMuted : ''} ${errors.phone ? styles.inputError : ''}`}>
                                <Controller name="phone" control={control} render={({
                field
              }) => <AppPhoneInput className={styles.phoneInput} value={field.value || undefined} onChange={value => field.onChange(value || '')} placeholder={t('phone')} disabled={lockPhone} />} />
                            </div>
                            {errors.phone && <span className={styles.error}>
                                    {errors.phone.message}
                                </span>}
                        </div>

                        <div className={styles.field}>
                            <span id="lt-quote-city" className={styles.label}>
                                {t('city')}
                                <span className={styles.req} aria-hidden>
                                    *
                                </span>
                            </span>
                            <SearchSelect value={cityValue || ''} onChange={next => setValue('city', next, {
              shouldValidate: true
            })} search={searchCityOptions} locked={lockCity} invalid={!!errors.city} placeholder={t('cityPlaceholder')} hintText={t('citySearchHint')} loadingText={t('loadingCities')} noResultsText={t('cityNoResults')} otherLabel={t('cityOther')} aria-labelledby="lt-quote-city" inputClassName={styles.input} lockedInputClassName={styles.inputMuted} />
                            {errors.city && <span className={styles.error}>
                                    {errors.city.message}
                                </span>}
                        </div>

                        {isFromCard && <>
                                <label className={styles.field}>
                                    <span className={styles.label}>
                                        {t('workspace')}
                                    </span>
                                    <input className={`${styles.input} ${lockWorkspace ? styles.inputMuted : ''}`} readOnly={lockWorkspace} placeholder={t('workspace')} {...register('workspace')} />
                                </label>

                                <label className={styles.field}>
                                    <span className={styles.label}>
                                        {t('listing')}
                                    </span>
                                    <input className={`${styles.input} ${lockListing ? styles.inputMuted : ''}`} readOnly={lockListing} placeholder={t('listing')} {...register('listing')} />
                                </label>
                            </>}

                        {/* A div, not a label: a label would re-click the
                            trigger when an option inside it is picked. */}
                        <div className={styles.field}>
                            <span id="lt-quote-listing-type" className={styles.label}>
                                {t('listingType')}
                                <span className={styles.req} aria-hidden>
                                    *
                                </span>
                            </span>
                            <Controller name="listingTypeId" control={control} render={({
              field
            }) => <SelectMenu name={field.name} value={field.value || ''} onChange={next => setValue('listingTypeId', next, {
              shouldValidate: true
            })} onBlur={field.onBlur} disabled={lockListingType} invalid={!!errors.listingTypeId} placeholder={t('listingTypePlaceholder')} aria-labelledby="lt-quote-listing-type" options={listingTypeOptions} />} />
                            {errors.listingTypeId && <span className={styles.error}>
                                    {errors.listingTypeId.message}
                                </span>}
                        </div>

                        <div className={styles.field}>
                            <span id="lt-quote-guests" className={styles.label}>
                                {t('guests')}
                                <span className={styles.req} aria-hidden>
                                    *
                                </span>
                            </span>
                            <Controller name="guests" control={control} render={({
              field
            }) => <SelectMenu name={field.name} value={field.value || ''} onChange={next => setValue('guests', next, {
              shouldValidate: true
            })} onBlur={field.onBlur} invalid={!!errors.guests} placeholder={t('guestsPlaceholder')} aria-labelledby="lt-quote-guests" options={guestOptions} />} />
                            {errors.guests && <span className={styles.error}>
                                    {errors.guests.message}
                                </span>}
                        </div>

                        <div className={styles.field}>
                            <span className={styles.label}>
                                {t('startDate')}
                                <span className={styles.req} aria-hidden>
                                    *
                                </span>
                            </span>
                            <Controller name="startDate" control={control} render={({
              field
            }) => {
              const selected = field.value ? dayjs(field.value) : null;
              return <DateSelectorPopover open={dateOpen} onClose={() => setDateOpen(false)} value={selected} minDate={dayjs().startOf('day')} hideHeader weekdayStyle="short" placement="top" popoverClassName={styles.datePopover} onChange={date => {
                field.onChange(date.format('YYYY-MM-DD'));
              }} anchor={<button type="button" className={styles.dateTrigger} onClick={() => setDateOpen(v => !v)}>
                                                    <span className={field.value ? styles.dateValue : styles.datePlaceholder}>
                                                        {field.value ? dayjs(field.value).format('D MMM YYYY') : t('startDatePlaceholder')}
                                                    </span>
                                                    <Calendar size={18} className={styles.dateIcon} aria-hidden />
                                                </button>} />;
            }} />
                            {errors.startDate && <span className={styles.error}>
                                    {errors.startDate.message}
                                </span>}
                        </div>

                        {submitError && <p className={styles.error}>{submitError}</p>}

                        <button type="submit" className={styles.submit} disabled={submitting}>
                            {submitting ? t('submitting') : t('submit')}
                        </button>
                    </form>}
            </div>
            </div>
        </div>;
  if (isInline) {
    return <div className={isPage ? styles.pageRoot : styles.inlineRoot}>
                {content}
            </div>;
  }
  return createPortal(<div className={styles.backdrop} onMouseDown={event => {
    if (event.target === event.currentTarget) onClose();
  }}>
            {content}
        </div>, document.body);
};
export default LongTermBookingModal;